"use client";

import { useMemo } from "react";
import type { MouseEvent } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBatchTasks } from "../context";
import { getModelDisplayLabel } from "../helpers/modelHelpers";

const PromptCell: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-sm leading-5 line-clamp-3 break-words max-w-[400px] overflow-hidden">{text}</div>
);

const BatchTaskTable: React.FC = () => {
  const {
    tasks,
    selectedTaskIds,
    selectionAnchorId,
    setSelection,
    addToSelection,
    removeFromSelection,
    setSelectionAnchor,
    removeTasks,
  } = useBatchTasks();

  const selectedSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);
  const selectAllChecked = tasks.length > 0 && selectedTaskIds.length === tasks.length;
  const selectAllIndeterminate =
    selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length;

  const getRangeIds = (anchorId: string, targetId: string): string[] => {
    const anchorIndex = tasks.findIndex((task) => task.id === anchorId);
    const targetIndex = tasks.findIndex((task) => task.id === targetId);
    if (anchorIndex < 0 || targetIndex < 0) return [targetId];
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    return tasks.slice(start, end + 1).map((task) => task.id);
  };

  const handleToggle = (taskId: string, event: MouseEvent<HTMLElement>) => {
    const additive = event.metaKey || event.ctrlKey;
    if (event.shiftKey && selectionAnchorId) {
      const rangeIds = getRangeIds(selectionAnchorId, taskId);
      if (additive) {
        addToSelection(rangeIds);
      } else {
        setSelection(rangeIds, taskId);
      }
      return;
    }

    if (selectedSet.has(taskId)) {
      if (additive) {
        removeFromSelection([taskId]);
      } else {
        setSelection([taskId], taskId);
      }
    } else {
      if (additive) {
        addToSelection([taskId]);
        setSelectionAnchor(taskId);
      } else {
        setSelection([taskId], taskId);
      }
    }
  };

  const handleRowClick = (taskId: string) => (event: MouseEvent<HTMLTableRowElement>) => {
    event.preventDefault();
    handleToggle(taskId, event);
  };

  const handleCheckboxClick = (taskId: string) => (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleToggle(taskId, event as unknown as MouseEvent<HTMLElement>);
  };

  const handleRemoveTask = (taskId: string) => (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    removeTasks([taskId]);
  };

  const handleSelectAll = (nextChecked: boolean) => {
    if (nextChecked) setSelection(tasks.map((task) => task.id));
    else setSelection([]);
  };

  const getStatusMeta = (task: typeof tasks[number]) => {
    if (!task.modelKey) {
      return { label: "待配置", color: "warning.main" as const };
    }
    switch (task.status) {
      case "pending":
        return { label: "提交中", color: "primary.main" as const };
      case "queued":
        return { label: "已排队", color: "info.main" as const };
      case "success":
        return { label: "已提交", color: "success.main" as const };
      case "failed":
        return { label: "提交失败", color: "error.main" as const };
      case "idle":
      default:
        return { label: "待提交", color: "text.secondary" as const };
    }
  };

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0 p-3">
      <div className="text-base font-semibold">导入列表</div>

      <div className="flex-1 min-h-0 border rounded-md overflow-x-auto overflow-y-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectAllChecked ? true : selectAllIndeterminate ? ("indeterminate" as any) : false}
                  onCheckedChange={(v) => handleSelectAll(Boolean(v))}
                />
              </TableHead>
              <TableHead className="w-[72px]">序号</TableHead>
              <TableHead className="min-w-[200px] max-w-[400px]">Prompt</TableHead>
              <TableHead className="w-[140px]">生成方案</TableHead>
              <TableHead className="w-[120px]">参考素材</TableHead>
              <TableHead className="w-[180px]">状态</TableHead>
              <TableHead className="w-[60px] text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="text-center text-sm text-muted-foreground py-8">暂无任务，请先导入 CSV。</div>
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task, index) => {
                const selected = selectedSet.has(task.id);
                const modelLabel = getModelDisplayLabel(task.modelKey);
                const imageCount = task.images.length;
                const statusMeta = getStatusMeta(task);
                const csvPosition =
                  typeof task.meta?.csvIndex === "number" ? task.meta.csvIndex : task.meta?.csvLine;

                return (
                  <TableRow
                    key={task.id}
                    data-state={selected ? "selected" : undefined}
                    onClick={handleRowClick(task.id)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => {}}
                        onClick={handleCheckboxClick(task.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <div className="text-sm font-semibold">{index + 1}</div>
                        {csvPosition ? (
                          <div className="text-xs text-muted-foreground">CSV 序号 {csvPosition}</div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PromptCell text={task.prompt} />
                    </TableCell>
                    <TableCell>
                      <div className={modelLabel ? "text-sm" : "text-sm text-muted-foreground"}>
                        {modelLabel ?? "未选择"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={imageCount ? "text-sm" : "text-sm text-muted-foreground"}>
                        {imageCount ? `共 ${imageCount} 张` : "未指定"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <div className="text-sm" style={{ color: "inherit" }}>{statusMeta.label}</div>
                        {task.taskId ? (
                          <div className="text-xs text-muted-foreground">ID: {task.taskId}</div>
                        ) : null}
                        {task.error ? (
                          <div className="text-xs text-red-600">{task.error}</div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={handleRemoveTask(task.id)}
                            aria-label="删除该任务"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>删除该任务</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default BatchTaskTable;
